import { faCheckCircle, faWarehouse } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { RawDraftContentState } from 'draft-js';
import { Fragment, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from 'urql';
import { ReadOnlyEditor } from '~/components/form/editor/ReadOnlyEditor';
import { MemoGallery } from '~/components/job/JobView/Feed/Gallery';
import { DateDisplay } from '~/components/sites/tabs/DateDisplay';
import { Avatar } from '~/components/ui/Avatar';
import { Image } from '~/components/ui/Image';
import { Loading } from '~/components/ui/Loading';
import {
  AssetHistoryDocument,
  type AssetHistoryQuery,
} from '~/generated/graphql';
import { formatDate, LONG_FORMAT } from '~/helpers/formatDate';
import { useAssetDeployments } from './AssetDeployments';
import {
  buildAssetHistoryItems,
  filterHistoryBySharingPermissions,
  type AssetHistoryActivityNode,
  type CompletedJobGroup,
  type DeploymentItem,
  type HistoryItem,
  type HistorySharingPermissions,
} from './assetHistoryItems';

// Custom hook to fetch and combine asset history
// Waits for BOTH queries to complete before showing any data to prevent
// the confusing UX of deployments appearing first and job history appearing later
export function useAssetHistory(
  assetId: string,
  enabled = true,
  jobId?: string,
  taskId?: string
) {
  // Fetch activity data
  const [activityResult, reexecuteActivityQuery] = useQuery<AssetHistoryQuery>({
    query: AssetHistoryDocument,
    variables: {
      assetIds: [assetId],
      first: 100,
      jobId: jobId || undefined,
      taskId: taskId || undefined,
    },
    requestPolicy: 'cache-first',
    pause: !assetId || !enabled,
  });

  // Fetch deployments data
  const [deployments, , deploymentsFetching, deploymentsError] =
    useAssetDeployments(assetId, enabled, jobId, taskId);

  // Track errors for each query
  const activityError = activityResult.error;
  // deploymentsError is already extracted from useAssetDeployments

  // Track if activity query has completed at least once (even with empty results or errors)
  // A query is considered complete if it's not fetching AND (it has data OR an error)
  const activityQueryCompleted =
    !activityResult.fetching &&
    (activityResult.data !== undefined || activityError !== undefined);
  // For deployments, since it always returns an array, we check: enabled AND not fetching
  // If enabled is false, query is paused (not complete)
  // If enabled is true and not fetching, query has completed (with data or error)
  const deploymentsQueryCompleted = enabled && !deploymentsFetching;

  // Both queries must be complete before we show any data
  // This prevents the jarring UX where deployments appear first, making users
  // think there's no job history, then job history pops in later
  const bothQueriesComplete =
    activityQueryCompleted && deploymentsQueryCompleted;

  // Process activity data - group by completed jobs
  // This useMemo processes up to 100 activity items, so it must be efficient
  // Only compute when both queries are complete to avoid partial renders
  const historyItems = useMemo<HistoryItem[]>(() => {
    // Return empty array until both queries complete
    if (!bothQueriesComplete) {
      return [];
    }

    const activityEdges = (activityResult.data?.allActivity?.edges ?? []).map(
      (edge) => ({
        node: edge.node as AssetHistoryActivityNode,
      })
    );

    return buildAssetHistoryItems(activityEdges, deployments);
  }, [
    activityResult.data?.allActivity?.edges,
    deployments,
    bothQueriesComplete,
  ]);

  // Combined fetching state - true if EITHER query is still loading
  const fetching = activityResult.fetching || deploymentsFetching;

  return [
    historyItems,
    () => reexecuteActivityQuery({ requestPolicy: 'network-only' }),
    fetching,
    activityResult.data?.allActivity?.pageInfo?.hasNextPage ?? false,
    activityError,
    deploymentsError,
    // TODO: Implement "Load More" pagination UI when hasNextPage is true
    // Currently limited to first 100 activity items for performance
  ] as const;
}

// Props for the main component
type AssetHistoryProps = {
  assetId: string;
  showSkeleton?: boolean;
  /** Controls whether queries should execute. When false, queries are paused to avoid unnecessary network requests. */
  enabled?: boolean;
  /** Optional job ID to filter history items for a specific job */
  jobId?: string;
  /** Task ID for task-specific permission checks (takes precedence over jobId) */
  taskId?: string;
  /**
   * When viewing from a job task context with external user permissions,
   * these control which history items are visible.
   * - shareJobHistory: Show completed jobs (status: Complete only)
   * - shareDeploymentHistory: Show deployment history
   */
  sharingPermissions?: HistorySharingPermissions;
};

// Main AssetHistory component
export function AssetHistory({
  assetId,
  showSkeleton,
  enabled = true,
  jobId,
  taskId,
  sharingPermissions,
}: AssetHistoryProps) {
  const { t } = useTranslation();
  // Pass enabled prop to control when queries execute - prevents immediate query execution on tab click
  // Hook now waits for BOTH queries to complete before returning data
  const [historyItems, , fetching, , activityError, deploymentsError] =
    useAssetHistory(assetId, enabled, jobId, taskId);

  // Filter history items based on sharing permissions
  const filteredHistoryItems = useMemo(
    () => filterHistoryBySharingPermissions(historyItems, sharingPermissions),
    [historyItems, sharingPermissions]
  );

  // Group items by date for display
  type GroupedItem = string | HistoryItem;
  const groupedItems = useMemo<GroupedItem[]>(() => {
    const result: GroupedItem[] = [];
    let currentDate: string | null = null;

    filteredHistoryItems.forEach((item) => {
      const itemDate = formatDate(item.timestamp, 'yyyy-MM-dd');

      // Add date header if it's a new date
      if (itemDate && itemDate !== currentDate) {
        result.push(itemDate);
        currentDate = itemDate;
      }

      result.push(item);
    });

    return result;
  }, [filteredHistoryItems]);

  // Show skeleton when queries haven't started yet
  if (showSkeleton) {
    return (
      <div className='flex h-48 items-center justify-center'>
        <Loading spinner />
      </div>
    );
  }

  // Show loading state while either query is still fetching
  // This ensures we don't show partial data (e.g., only deployments)
  // which could mislead users into thinking there's no job history
  if (fetching) {
    return (
      <div className='flex h-48 items-center justify-center'>
        <Loading spinner />
      </div>
    );
  }

  // Show error state if either query has an error
  if (activityError || deploymentsError) {
    return (
      <div className='flex flex-col items-center justify-center p-8'>
        <p className='mb-2 text-secondary'>{t('Error loading history')}</p>
        <p className='text-sm text-tertiary'>
          {activityError && deploymentsError
            ? t('Failed to load job history and deployments')
            : activityError
            ? t('Failed to load job history')
            : t('Failed to load deployments')}
        </p>
      </div>
    );
  }

  // Show empty state when both queries have finished and there's no data
  if (filteredHistoryItems.length === 0) {
    return (
      <div className='flex flex-col items-center justify-center p-8'>
        <p className='mb-2 text-secondary'>{t('No history found')}</p>
        <p className='text-sm text-tertiary'>
          {t('Completed jobs and deployments for this asset will appear here')}
        </p>
      </div>
    );
  }

  return (
    <div className='space-y-2 px-3 pb-3'>
      {groupedItems.map((item) => (
        <Fragment
          key={
            typeof item === 'string'
              ? `date-${item}`
              : item.type === 'completedJob'
              ? `job-${item.data.jobId}`
              : `deployment-${item.data.id}`
          }
        >
          {typeof item === 'string' ? (
            <DateDisplay date={item} />
          ) : (
            <HistoryTimelineItem item={item} />
          )}
        </Fragment>
      ))}
    </div>
  );
}

// Sub-component for rendering individual history items
type HistoryTimelineItemProps = {
  item: HistoryItem;
};

function HistoryTimelineItem({ item }: HistoryTimelineItemProps) {
  if (item.type === 'deployment') {
    return <DeploymentHistoryItem deployment={item.data} />;
  }

  return <CompletedJobHistoryItem job={item.data} />;
}

// Component for rendering completed job history items - all in one card
type CompletedJobHistoryItemProps = {
  job: CompletedJobGroup;
};

function CompletedJobHistoryItem({ job }: CompletedJobHistoryItemProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { siteId, assetId } = useParams();

  // Handle job click - navigate to job view
  const handleJobClick = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');

    // If we're in a site context, use site-scoped job route
    // This allows staying within the site context instead of drilling down to asset
    let baseUrl: string;
    if (siteId) {
      baseUrl = `/sites/${siteId}/jobs/${job.jobId}`;
    } else if (assetId) {
      baseUrl = `/assets/${assetId}/jobs/${job.jobId}`;
    } else {
      // Fallback when not in a site/asset route (e.g. generic jobs view)
      baseUrl = `/jobs/${job.jobId}`;
    }

    const jobUrl = tabParam ? `${baseUrl}?tab=${tabParam}` : baseUrl;

    // Build a returnTo state so job close returns back to the asset tab view
    // CRITICAL FIX: When navigating between jobs, preserve the original asset view path
    // instead of creating a chain of job-to-job navigation
    const path = location.pathname + location.search;
    const isAssetContext = path.includes('/assets/');

    let linkState: { returnTo?: string } | undefined;
    if (isAssetContext) {
      // If we're already in a job view (navigating from one job to another),
      // use the returnTo from the current location state to preserve the original asset path
      const currentState = location.state as { returnTo?: string } | null;
      if (currentState?.returnTo && path.includes('/jobs/')) {
        linkState = { returnTo: currentState.returnTo };
      } else if (path.includes('/jobs/')) {
        // If we're in a job view but don't have returnTo in state,
        // extract the asset path from the current pathname
        const assetMatch = path.match(
          /^(\/assets\/[^/]+)(?:\/jobs\/.*)?(\?.*)?$/
        );
        if (assetMatch) {
          const assetPath = assetMatch[1];
          const searchParams = assetMatch[2] || '';
          // Preserve tab parameter if present
          const finalSearch = tabParam ? `?tab=${tabParam}` : searchParams;
          linkState = { returnTo: assetPath + finalSearch };
        } else {
          linkState = { returnTo: path };
        }
      } else {
        // Default: use current path (when navigating from asset view directly)
        linkState = { returnTo: path };
      }
    }

    navigate(jobUrl, { state: linkState });
  }, [navigate, location, siteId, assetId, job.jobId]);

  return (
    <div
      className='cursor-pointer rounded-lg border border-outline-variant bg-white shadow-sm transition-shadow hover:shadow-md'
      onClick={handleJobClick}
      role='button'
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleJobClick();
        }
      }}
      aria-label={`${t('View job')}: ${job.jobName}`}
    >
      {/* Header - Job completion info */}
      <div className='flex items-start gap-3 border-b border-outline-variant p-4'>
        <div
          className={classNames(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-status-complete'
          )}
        >
          <FontAwesomeIcon
            icon={faCheckCircle}
            className='h-5 w-5 text-white'
          />
        </div>
        <div className='min-w-0 flex-1'>
          <div className='flex items-center gap-2'>
            <span className='font-semibold text-primary'>{job.jobName}</span>
            <span className='rounded bg-status-complete/10 px-2 py-0.5 text-xs font-medium text-status-complete'>
              {t('Completed')}
            </span>
          </div>
          {job.siteName && (
            <p className='mt-0.5 text-sm text-secondary'>{job.siteName}</p>
          )}
          <p className='mt-1 text-xs text-tertiary'>
            {formatDate(job.completedAt, LONG_FORMAT)}
            {job.completedBy && ` • ${job.completedBy.name}`}
          </p>
        </div>
      </div>

      {/* Content - Assignee notes, task notes, and messages in separate sections */}
      {(job.assigneeNotes ||
        job.taskNotes.length > 0 ||
        job.messages.length > 0) && (
        <div className='p-4'>
          {/* Assignee Notes Section - notes added when completing the job */}
          {job.assigneeNotes && (
            <div className='space-y-3'>
              <p className='text-xs font-medium uppercase text-tertiary'>
                {t('Assignee Notes')}
              </p>
              <AssigneeNotesContent assigneeNotes={job.assigneeNotes} />
            </div>
          )}

          {/* Task Notes Section */}
          {job.taskNotes.length > 0 && (
            <div
              className={classNames(
                'space-y-3',
                job.assigneeNotes && 'mt-4 border-t border-outline-variant pt-4'
              )}
            >
              <p className='text-xs font-medium uppercase text-tertiary'>
                {t('Task Notes')}
              </p>
              {job.taskNotes.map((task, idx) => (
                <TaskNoteItem
                  key={task.taskId ? `task-${task.taskId}` : `task-${idx}`}
                  task={task}
                />
              ))}
            </div>
          )}

          {/* Messages Section */}
          {job.messages.length > 0 && (
            <div
              className={classNames(
                'space-y-3',
                (job.assigneeNotes || job.taskNotes.length > 0) &&
                  'mt-4 border-t border-outline-variant pt-4'
              )}
            >
              <p className='text-xs font-medium uppercase text-tertiary'>
                {t('Messages')}
              </p>
              {job.messages.map((message) => (
                <MessageItem key={message.id} message={message} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Assignee notes content component - handles JSON parsing
type AssigneeNotesContentProps = {
  assigneeNotes: string;
};

function AssigneeNotesContent({ assigneeNotes }: AssigneeNotesContentProps) {
  // Parse assignee notes if it's JSON (draft-js format)
  const parsedContent = useMemo(() => {
    if (!assigneeNotes) return null;
    try {
      const parsed = JSON.parse(assigneeNotes) as RawDraftContentState;
      return parsed?.blocks ? parsed : null;
    } catch {
      return null;
    }
  }, [assigneeNotes]);

  return (
    <div className='border-l-2 border-status-complete bg-status-complete/5 px-3 py-2'>
      {parsedContent ? (
        <div className='text-sm'>
          <ReadOnlyEditor content={parsedContent} />
        </div>
      ) : (
        <p className='text-sm text-primary'>{assigneeNotes}</p>
      )}
    </div>
  );
}

// Task note item within a completed job
type TaskNoteItemProps = {
  task: CompletedJobGroup['taskNotes'][0];
};

function TaskNoteItem({ task }: TaskNoteItemProps) {
  return (
    <div className='border-l-2 border-brand bg-surface-container-low px-3 py-2'>
      <p className='text-xs font-medium text-tertiary'>{task.taskName}</p>
      {task.notes && <p className='mt-1 text-sm text-primary'>{task.notes}</p>}
      {task.attachments && task.attachments.length > 0 && (
        <div className='mt-2'>
          <MemoGallery attachments={task.attachments} />
        </div>
      )}
    </div>
  );
}

// Message item within a completed job
type MessageItemProps = {
  message: CompletedJobGroup['messages'][0];
};

function MessageItem({ message }: MessageItemProps) {
  // Parse comment body if it's JSON (draft-js format)
  const parsedBody = useMemo(() => {
    const body = message.body;
    if (!body) return null;
    try {
      const parsed = JSON.parse(body) as RawDraftContentState;
      return parsed?.blocks ? parsed : null;
    } catch {
      return null;
    }
  }, [message.body]);

  return (
    <div className='flex items-start gap-2'>
      <Avatar
        size='small'
        image={message.createdBy?.image}
        name={message.createdBy?.name}
      />
      <div className='min-w-0 flex-1'>
        {message.createdBy && (
          <p className='text-xs font-medium text-secondary'>
            {message.createdBy.name}
          </p>
        )}
        {parsedBody ? (
          <div className='text-sm'>
            <ReadOnlyEditor content={parsedBody} />
          </div>
        ) : message.body ? (
          <p className='text-sm text-primary'>{message.body}</p>
        ) : null}
        {message.attachments && message.attachments.length > 0 && (
          <div className='mt-2'>
            <MemoGallery attachments={message.attachments} />
          </div>
        )}
      </div>
    </div>
  );
}

// Component for rendering deployment history items
type DeploymentHistoryItemProps = {
  deployment: DeploymentItem;
};

function DeploymentHistoryItem({ deployment }: DeploymentHistoryItemProps) {
  const { t } = useTranslation();

  return (
    <div className='rounded-lg border border-outline-variant bg-white shadow-sm'>
      <div className='flex items-start gap-3 p-4'>
        {deployment.image ? (
          <div className='border-1 flex h-10 w-10 shrink-0 items-center justify-center overflow-clip rounded-full border border-outline-variant'>
            <Image
              url={deployment.image}
              size='fit'
              alt={deployment.displayLocation || 'Deployment location'}
            />
          </div>
        ) : (
          <div
            className={classNames(
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-lighter'
            )}
          >
            <FontAwesomeIcon
              icon={faWarehouse}
              className='h-5 w-5 text-white'
            />
          </div>
        )}
        {/* Icon */}

        {/* Content */}
        <div className='min-w-0 flex-1'>
          <div className='flex items-center gap-2'>
            <span className='font-semibold text-primary'>{t('Deployed')}</span>
          </div>
          {deployment.displayLocation && (
            <p className='mt-0.5 text-sm text-secondary'>
              {deployment.displayLocation}
            </p>
          )}
          <p className='mt-1 text-xs text-tertiary'>
            {formatDate(deployment.startedAt, LONG_FORMAT)}
          </p>
        </div>
      </div>
    </div>
  );
}

// Export types for external use
export type { DeploymentItem, HistoryItem } from './assetHistoryItems';
